import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { stripCharacterUgcImages } from './characterUgc';
import type { CharacterUgcConfig, CharacterUgcRole } from './characterUgc';

export type Tab = 'chat' | 'masterPrompt' | 'flowPanel' | 'agentGraph' | 'youwee' | 'normalPanel' | 'tiktok' | 'users' | 'characterFilms' | 'flowkitDashboard' | 'ugcFlowkit' | 'publishing';
export type ChatProvider = 'claude' | 'gemini';

export type CliProvider = 'codex' | 'claude' | 'gemini';
export type VideoMode = 'idea' | 'script';
export type LibraryMode = 'ai' | 'hybrid' | 'library';
export type AspectRatio = '16:9' | '9:16' | '1:1' | '4:3';
export type VideoQuality = '720p' | '1080p' | '4K';
export type UgcMode = 'faceless' | 'character';
export type Engine = 'flowboard' | 'flowkit' | 'antigravity';

export interface PromptJob {
  id: string;
  studioJobId?: string;    // original pipeline callback ID, retained through Normal Panel completion
  autoMerge?: boolean;
  idea: string;
  productName?: string;   // parsed "Product Name:" line from the master prompt — used for merge/scene titles
  technique: string;
  material: string;
  duration: number;
  language: string;
  aspect: AspectRatio;
  progress: number;
  status: 'pending' | 'running' | 'done' | 'error';
  category: string;
  review?: string;
  script?: string;  // full generated script content
  shotsCreated?: boolean;   // true once shots have been parsed — prevents re-processing
  // Per-job product images (used for all shots of this UGC — avoids per-shot assignment)
  jobImageUrls?: string[];
  jobImageFiles?: string[];
  rpcReferenceHashes?: string[]; // verify reattachments without persisting image bytes
  // Shared Flowboard context — created once for the whole job
  boardId?: number;          // the one board all shots share
  flowProjectId?: string;    // bound Google Flow project
  sceneId?: string;          // shared Google Flow sceneId (groups all shot clips as a scene)
  sceneNodeId?: number;      // final scene node id
  sceneRequestId?: number;   // merge_video request id
  // Shared VA nodes — created once per job, reused by every shot
  vaNodeIds?: number[];      // visual_asset node IDs (product images)
  vaMediaIds?: string[];     // uploaded image media IDs in Flow (same order as vaNodeIds)
  // Character UGC — uploaded once per job
  charMediaId?: string;      // uploaded character reference media_id (character mode only)
  ugcMode?: UgcMode;
  characterUgc?: CharacterUgcConfig;
  characterUgcReferences?: {
    projectId: string;
    mediaByRole: Partial<Record<CharacterUgcRole, { mediaId: string; imageSha256: string }>>;
  };
  characterName?: string;
  characterDesc?: string;
  characterImageUrl?: string;
  characterImageFile?: string;
  characterProfile?: Record<string, unknown>;
  // Shared Flowkit context — created once for the whole job (mirrors the
  // Flowboard fields above). No `engine` field here — engine is a single
  // global setting locked while any shot is generating, so a job's shots
  // are always homogeneous by construction (see ShotItem.engine).
  fkProjectId?: string;
  fkVideoId?: string;
  fkVaEntityIds?: string[];
  fkVaEntityNames?: string[];    // the exact `name` strings used to create each entity — reused as character_names on scenes
  fkVaMediaIds?: string[];
  fkReferenceCount?: number;    // expected count survives reload during partial uploads
  fkCharEntityId?: string;
  fkCharEntityName?: string;
  fkCharMediaId?: string;
  fkMergedVideoUrl?: string;     // set after a successful Flowkit merge
  fkMergePending?: boolean;
  fkMergeError?: string;          // blocks automatic retry until the user explicitly retries the merge
  // DDlab-owned Flowboard batch-RPC context. These references are uploaded
  // once per job and reused by every shot routed through the Flowboard engine.
  batchRpcProjectId?: string;
  batchRpcVideoId?: string;
  batchRpcReferenceEntityNames?: string[];
  batchRpcReferenceMediaIds?: string[];
  batchRpcReferenceCount?: number;
  batchRpcCharacterMediaId?: string;
}

export interface ShotItem {
  id: string;
  jobId: string;
  shotNumber: number;
  prompt: string;
  voiceover: string;
  durationSec: number;
  characterRoles?: CharacterUgcRole[];
  characterUgcVersion?: 'couple-ugc-v1';
  selected: boolean;
  imageDataUrls: string[];   // base64 previews for UI
  imageFiles: string[];      // file paths / names for upload
  status: 'pending' | 'uploading' | 'ready' | 'generating' | 'done' | 'error';
  errorMsg?: string;
  boardId?: number;
  nodeIds?: { visualAssets: number[]; video: number };
  requestId?: number;
  videoUrl?: string;
  // Stamped at generation time — which engine actually produced this shot.
  // Used by the auto-merge handler to route to the matching merge path.
  engine?: Engine;
  fkSceneId?: string;
  fkImageRequestId?: string;   // Flowkit request ids are UUID strings, unlike Flowboard's numeric requestId
  fkVideoRequestId?: string;
  rpcRequestEngine?: 'flowkit' | 'flowboard';
  rpcSubmissionPending?: boolean; // lost submit reply must not trigger another paid render
}

export interface ChatMessageImage {
  b64?: string;
  url?: string;
  mime?: string;
  alt?: string;
  isGenerated?: boolean;
}

export interface ChatMessageVideo {
  url: string;
  title: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  provider: ChatProvider;
  timestamp: number;
  images?: ChatMessageImage[];
  thoughts?: string;
  videos?: ChatMessageVideo[];
}

export interface StoreState {
  // Auth
  token: string | null;
  user: { username: string; role?: 'admin' | 'user' } | null;
  setAuth: (token: string, user: { username: string; role?: 'admin' | 'user' }) => void;
  logout: () => void;
  
  // Navigation
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
  ugcFlowkitBatchId: string | null;
  setUgcFlowkitBatchId: (batchId: string | null) => void;
  
  // Chat
  activeProvider: ChatProvider;
  setActiveProvider: (provider: ChatProvider) => void;
  chatMessages: Record<ChatProvider, ChatMessage[]>;
  addMessage: (provider: ChatProvider, msg: ChatMessage) => void;
  updateLastAssistant: (provider: ChatProvider, content: string) => void;
  appendImageToLastAssistant: (provider: ChatProvider, img: ChatMessageImage) => void;
  appendThoughtsToLastAssistant: (provider: ChatProvider, text: string) => void;
  appendVideoToLastAssistant: (provider: ChatProvider, video: ChatMessageVideo) => void;
  clearChat: (provider: ChatProvider) => void;
  geminiAuthStatus: 'unknown' | 'ok' | 'missing';
  setGeminiAuthStatus: (s: 'unknown' | 'ok' | 'missing') => void;
  
  // Code
  pendingChanges: any[];
  setPendingChanges: (changes: any[]) => void;

  // Master Prompt — shot processing queue
  shots: ShotItem[];
  processingJobId: string | null;
  autoMerge: boolean;
  setShots: (shots: ShotItem[]) => void;
  addShots: (shots: ShotItem[]) => void;
  updateShot: (id: string, patch: Partial<ShotItem>) => void;
  removeShot: (id: string) => void;
  clearShots: () => void;
  toggleShotSelected: (id: string) => void;
  selectAllShots: (selected: boolean) => void;
  setProcessingJobId: (id: string | null) => void;
  setAutoMerge: (v: boolean) => void;

  // Auto Mode
  autoMode: boolean;
  setAutoMode: (v: boolean) => void;
  autoProductImageUrls: string[];
  autoProductFileNames: string[];
  setAutoProductImages: (urls: string[], names: string[]) => void;
  autoGeneratePending: boolean;
  setAutoGeneratePending: (v: boolean) => void;
  autoGenerateJobIds: string[];
  queueAutoGenerateJob: (jobId: string) => void;
  consumeAutoGenerateJob: () => void;
  autoProcessPending: boolean;
  setAutoProcessPending: (v: boolean) => void;

  // Master Prompt config
  mpCliProvider: CliProvider;
  setMpCliProvider: (p: CliProvider) => void;
  mpVideoMode: VideoMode;
  setMpVideoMode: (m: VideoMode) => void;
  mpLibraryMode: LibraryMode;
  setMpLibraryMode: (m: LibraryMode) => void;
  mpAspect: AspectRatio;
  setMpAspect: (a: AspectRatio) => void;
  mpQuality: VideoQuality;
  setMpQuality: (q: VideoQuality) => void;
  mpDuration: number;
  setMpDuration: (d: number) => void;
  mpLanguage: string;
  setMpLanguage: (l: string) => void;
  mpFolder: string;
  setMpFolder: (f: string) => void;
  mpJobs: PromptJob[];
  addMpJob: (job: PromptJob) => void;
  removeMpJob: (id: string) => void;
  updateMpJob: (id: string, patch: Partial<PromptJob>) => void;
  clearMpJobs: () => void;
  // UGC mode selector
  mpUgcMode: UgcMode;
  setMpUgcMode: (m: UgcMode) => void;
  mpCharacterUgc: CharacterUgcConfig;
  setMpCharacterUgc: (config: CharacterUgcConfig) => void;
  // Character profile (for character UGC mode)
  mpCharacterName: string;
  setMpCharacterName: (n: string) => void;
  mpCharacterDesc: string;
  setMpCharacterDesc: (d: string) => void;
  mpCharacterImageUrl: string;   // base64 data URL for preview (not persisted)
  mpCharacterImageFile: string;  // filename
  setMpCharacterImage: (url: string, file: string) => void;
  // Generation engine — global switch, locked in the UI while any shot is
  // actively generating/queued (see NormalPanelTab/MasterPromptTab).
  mpEngine: Engine;
  setMpEngine: (e: Engine) => void;
  // UGC Master version switch: 'v1' (classic) | 'v2' (inline flowkit pipeline) | 'v3' (affiliate harness)
  ugcMasterVersion: 'v1' | 'v2' | 'v3';
  setUgcMasterVersion: (v: 'v1' | 'v2' | 'v3') => void;
}

export const useStore = create<StoreState>()(
  persist(
    (set, get) => ({
      // Auth
      token: null,
      user: null,
      setAuth: (token, user) => set({ token, user }),
      logout: () => set({ token: null, user: null }),
      
      // Navigation
      activeTab: 'masterPrompt',
      setActiveTab: (tab) => set({ activeTab: tab }),
      ugcFlowkitBatchId: null,
      setUgcFlowkitBatchId: (batchId) => set({ ugcFlowkitBatchId: batchId }),
      
      // Chat
      activeProvider: 'claude',
      setActiveProvider: (provider) => set({ activeProvider: provider }),
      chatMessages: { claude: [], gemini: [] },
      geminiAuthStatus: 'unknown',
      setGeminiAuthStatus: (s) => set({ geminiAuthStatus: s }),
      addMessage: (provider, msg) => set(state => ({
        chatMessages: {
          ...state.chatMessages,
          [provider]: [...state.chatMessages[provider], msg]
        }
      })),
      updateLastAssistant: (provider, content) => set(state => {
        const msgs = [...state.chatMessages[provider]];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
          msgs[lastIdx] = { ...msgs[lastIdx], content };
        }
        return { chatMessages: { ...state.chatMessages, [provider]: msgs } };
      }),
      appendImageToLastAssistant: (provider, img) => set(state => {
        const msgs = [...state.chatMessages[provider]];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
          const prev = msgs[lastIdx];
          msgs[lastIdx] = { ...prev, images: [...(prev.images || []), img] };
        }
        return { chatMessages: { ...state.chatMessages, [provider]: msgs } };
      }),
      appendThoughtsToLastAssistant: (provider, text) => set(state => {
        const msgs = [...state.chatMessages[provider]];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
          const prev = msgs[lastIdx];
          msgs[lastIdx] = { ...prev, thoughts: (prev.thoughts || '') + text };
        }
        return { chatMessages: { ...state.chatMessages, [provider]: msgs } };
      }),
      appendVideoToLastAssistant: (provider, video) => set(state => {
        const msgs = [...state.chatMessages[provider]];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
          const prev = msgs[lastIdx];
          msgs[lastIdx] = { ...prev, videos: [...(prev.videos || []), video] };
        }
        return { chatMessages: { ...state.chatMessages, [provider]: msgs } };
      }),
      clearChat: (provider) => set(state => ({
        chatMessages: { ...state.chatMessages, [provider]: [] }
      })),
      
      // Code
      pendingChanges: [],
      setPendingChanges: (changes) => set({ pendingChanges: changes }),

      // Shot queue
      shots: [],
      processingJobId: null,
      autoMerge: false,
      setShots: (shots) => set({ shots }),
      addShots: (shots) => set(state => ({ shots: [...state.shots, ...shots] })),
      updateShot: (id, patch) => set(state => ({ shots: state.shots.map(s => s.id === id ? { ...s, ...patch } : s) })),
      removeShot: (id) => set(state => ({ shots: state.shots.filter(s => s.id !== id) })),
      clearShots: () => set({ shots: [] }),
      toggleShotSelected: (id) => set(state => ({ shots: state.shots.map(s => s.id === id ? { ...s, selected: !s.selected } : s) })),
      selectAllShots: (selected) => set(state => ({ shots: state.shots.map(s => ({ ...s, selected })) })),
      setProcessingJobId: (id) => set({ processingJobId: id }),
      setAutoMerge: (v) => set({ autoMerge: v }),

      autoMode: false,
      setAutoMode: (v) => set({ autoMode: v }),
      autoProductImageUrls: [],
      autoProductFileNames: [],
      setAutoProductImages: (urls, names) => set({ autoProductImageUrls: urls, autoProductFileNames: names }),
      autoGeneratePending: false,
      setAutoGeneratePending: (v) => set({ autoGeneratePending: v }),
      autoGenerateJobIds: [],
      queueAutoGenerateJob: (jobId) => set(state => state.autoGenerateJobIds.includes(jobId)
        ? state
        : { autoGenerateJobIds: [...state.autoGenerateJobIds, jobId] }),
      consumeAutoGenerateJob: () => set(state => ({ autoGenerateJobIds: state.autoGenerateJobIds.slice(1) })),
      autoProcessPending: false,
      setAutoProcessPending: (v) => set({ autoProcessPending: v }),

      // Master Prompt
      mpCliProvider: 'claude',
      setMpCliProvider: (p) => set({ mpCliProvider: p }),
      mpVideoMode: 'idea',
      setMpVideoMode: (m) => set({ mpVideoMode: m }),
      mpLibraryMode: 'ai',
      setMpLibraryMode: (m) => set({ mpLibraryMode: m }),
      mpAspect: '16:9',
      setMpAspect: (a) => set({ mpAspect: a }),
      mpQuality: '1080p',
      setMpQuality: (q) => set({ mpQuality: q }),
      mpDuration: 180,
      setMpDuration: (d) => set({ mpDuration: d }),
      mpLanguage: 'en',
      setMpLanguage: (l) => set({ mpLanguage: l }),
      mpFolder: '/',
      setMpFolder: (f) => set({ mpFolder: f }),
      mpJobs: [],
      addMpJob: (job) => set(state => ({ mpJobs: [...state.mpJobs, job] })),
      removeMpJob: (id) => set(state => ({ mpJobs: state.mpJobs.filter(j => j.id !== id) })),
      updateMpJob: (id, patch) => set(state => ({
        mpJobs: state.mpJobs.map(j => j.id === id ? { ...j, ...patch } : j)
      })),
      clearMpJobs: () => set({ mpJobs: [] }),
      // UGC mode
      mpUgcMode: 'faceless',
      setMpUgcMode: (m) => set({ mpUgcMode: m }),
      mpCharacterUgc: {
        version: 'couple-ugc-v1',
        characters: [
          { role: 'wife', name: 'Linh', appearance: 'Adult woman, shoulder-length black hair, casual neutral outfit.', voice: 'Warm, natural female voice.' },
          { role: 'husband', name: 'Minh', appearance: 'Adult man, short black hair, casual neutral outfit.', voice: 'Low, relaxed male voice.' },
        ],
      },
      setMpCharacterUgc: (config) => set({ mpCharacterUgc: config }),
      // Character profile
      mpCharacterName: '',
      setMpCharacterName: (n) => set({ mpCharacterName: n }),
      mpCharacterDesc: '',
      setMpCharacterDesc: (d) => set({ mpCharacterDesc: d }),
      mpCharacterImageUrl: '',
      mpCharacterImageFile: '',
      setMpCharacterImage: (url, file) => set({ mpCharacterImageUrl: url, mpCharacterImageFile: file }),
      // Generation engine
      mpEngine: 'flowboard',
      setMpEngine: (e) => set({ mpEngine: e }),
      // UGC Master version switch: default to v2
      ugcMasterVersion: 'v2',
      setUgcMasterVersion: (v) => set({ ugcMasterVersion: v }),
    }),
    {
      name: 'studio-store',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        // Active tab & provider
        activeTab: state.activeTab,
        ugcFlowkitBatchId: state.ugcFlowkitBatchId,
        ugcMasterVersion: state.ugcMasterVersion,
        activeProvider: state.activeProvider,
        // Chat history (Claude + Gemini)
        chatMessages: state.chatMessages,
        // Master Prompt configuration
        mpCliProvider: state.mpCliProvider,
        mpVideoMode: state.mpVideoMode,
        mpLibraryMode: state.mpLibraryMode,
        mpAspect: state.mpAspect,
        mpQuality: state.mpQuality,
        mpDuration: state.mpDuration,
        mpLanguage: state.mpLanguage,
        mpFolder: state.mpFolder,
        mpEngine: state.mpEngine,
        // UGC mode + character profile text (image URL is not persisted — too large)
        mpUgcMode: state.mpUgcMode,
        mpCharacterUgc: stripCharacterUgcImages(state.mpCharacterUgc),
        mpCharacterName: state.mpCharacterName,
        mpCharacterDesc: state.mpCharacterDesc,
        // Batch queue — images stripped (too large for localStorage; re-sent on each request)
        mpJobs: (state.mpJobs ?? []).map(j => ({
          ...j,
          ...(j.characterUgc ? { characterUgc: stripCharacterUgcImages(j.characterUgc) } : {}),
          jobImageUrls:  [],   // base64 images removed — present in-memory only during session
          jobImageFiles: [],
        })),
        // Shot queue — persisted WITHOUT imageDataUrls (too large for localStorage).
        // Transient error/generating states are reset so user can retry on reload.
        shots: (state.shots ?? []).map(s => ({
          ...s,
          imageDataUrls: [],                   // cleared — too large for localStorage
          // Reset non-final states so page reload = clean retry
          status: s.status === 'done' ? 'done' as const : 'pending' as const,
          errorMsg: s.status === 'error' ? undefined : s.errorMsg,
        })),
        processingJobId: state.processingJobId,
        autoMerge: state.autoMerge,
      })
    }
  )
);
